// useAuthCompleted — единая точка «вход завершён» для ВСЕХ способов входа
// (единый профиль экосистемы + админский вход паролем за `?admin=true`).
//
// Порядок шагов важен: сначала инвалидация auth.me (AuthFlowGate должен увидеть
// свежие pendingConsent/pendingOnboarding), затем broadcast в соседние вкладки,
// запись подсказки «последний способ» и только потом навигация.
// Consent/онбординг подхватит AuthFlowGate — здесь про них знать не нужно.

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { consumeAuthReturnUrl } from "@/lib/auth-return-url";
import { type LoginMethod, setLastLoginMethod } from "@/lib/last-login-method";
import { splitLocalePrefix } from "@/lib/locale-url";
import { broadcastAuthEvent } from "@/providers/auth-provider";
import { useLoginModalStore } from "@/stores/login-modal";

interface CompleteAuthOptions {
	/**
	 * Каким способом вошли — пишется в подсказку на экране входа.
	 * Не передаётся для админского входа паролем: это локальная учётка для
	 * тестов, а не единый профиль, и подсказку про него рисовать нечего.
	 */
	method?: LoginMethod;
	/**
	 * Куда вернуться. Если не передан — берётся одноразовый returnTo из
	 * localStorage (его пишут перед уходом на внешний провайдер).
	 */
	returnTo?: string;
	/** Не уводить с текущего роута (обработчик сам решит, куда идти). */
	skipNavigate?: boolean;
}

/**
 * Приводит путь возврата к виду, который понимает роутер.
 *
 * Кроме защиты от внешних адресов (`//evil.com`) снимает языковой префикс:
 * `returnTo` собирают из `window.location`, то есть из ПУБЛИЧНОГО адреса, а
 * `navigate({ to })` ждёт внутренний путь. Без этого вход со страницы
 * `/de/catalog/python-pro` возвращал бы на несуществующий маршрут, то есть на
 * 404 вместо той страницы, с которой человек уходил логиниться. Язык при этом
 * не теряется: префикс вернётся в адрес сам (rewrite роутера, `lib/locale-url`).
 */
function sanitize(path: string): string {
	if (!path.startsWith("/") || path.startsWith("//")) return "/";
	const [pathname = "/", rest = ""] = splitQuery(path);
	return `${splitLocalePrefix(pathname).path}${rest}`;
}

/** Делит `/de/catalog?area=math#top` на путь и хвост (запрос + якорь). */
function splitQuery(href: string): [string, string] {
	const cut = href.search(/[?#]/);
	return cut === -1 ? [href, ""] : [href.slice(0, cut), href.slice(cut)];
}

export function useAuthCompleted(): (options: CompleteAuthOptions) => Promise<void> {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	return useCallback(
		async ({ method, returnTo, skipNavigate }: CompleteAuthOptions) => {
			await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
			broadcastAuthEvent("login");
			if (method) setLastLoginMethod(method);
			useLoginModalStore.getState().close();

			// consumeAuthReturnUrl одноразов — вызываем и когда returnTo передан явно,
			// иначе «протухший» путь всплывёт при следующем входе.
			const stored = consumeAuthReturnUrl();
			if (skipNavigate) return;
			const target = sanitize(returnTo ?? stored);
			await navigate({ to: target as never, replace: true });
		},
		[navigate, queryClient],
	);
}
